import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useCreateExport } from 'api/hooks/use-exports'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faUserPlus,
  faRobot,
  faCheck,
  faBalanceScale
} from '@fortawesome/free-solid-svg-icons'
import Dropdown from 'react-bootstrap/Dropdown'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import FieldLabel from 'shared/forms/field-label'
import ShieldLogo from 'assets/Shield/White/ThirdLine Shield White.svg'

// Each option queues a server-side export scoped to the current employee. The
// `source` slug maps to the matching exporter in ExportWorker.
const EXPORT_OPTIONS = [
  { key: 'risk-profile', label: 'Risk Profile (PDF)', source: 'employeePdf', prefix: 'Risk Profile' },
  { key: 'conflicts', label: 'Conflicts', source: 'employeeConflicts', prefix: 'Conflicts' },
  { key: 'roles', label: 'Roles', source: 'employeeRoles', prefix: 'Roles' },
  {
    key: 'permissions',
    label: 'Permissions',
    source: 'employeePermissions',
    prefix: 'Permissions',
    // Match the Permissions tab's default of only showing expansive values.
    extraParams: { is_expansive: true }
  }
]

// Consolidates the user detail page's header actions — Add Permission, the
// System User toggle, and the per-table exports — into a single Actions
// dropdown matching the Roles#show pattern.
function ActionsDropdown({ employee, isSystem, onToggleSystemUser, onAddPermission, onCompareUser }) {
  const [show, setShow] = useState(false)
  const [exportParams, setExportParams] = useState(null)

  const { mutate } = useCreateExport({
    params: exportParams,
    options: {
      onSuccess: () => toast.success('File will be available in Exports shortly')
    }
  })

  const handleSelect = (option) => {
    setExportParams({
      name: `${option.prefix} - ${employee.external_id}`,
      source: option.source,
      status: 'queued',
      params: { employee_id: employee.id, ...(option.extraParams || {}) }
    })
    setShow(true)
  }

  const handleSubmit = () => {
    mutate()
    setShow(false)
    setExportParams(null)
  }

  return (
    <React.Fragment>
      <Dropdown className="d-inline-block ms-2">
        <Dropdown.Toggle variant="primary" id="employee-actions-dropdown">
          <img
            src={ShieldLogo}
            alt=""
            className="me-2"
            style={{ height: '1.5em', verticalAlign: '-0.4em' }}
          />
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu align="end">
          <Dropdown.Item onClick={onAddPermission}>
            <FontAwesomeIcon
              icon={faUserPlus}
              fixedWidth
              className="me-2 text-muted"
            />
            Add Permission
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onToggleSystemUser(!isSystem)}>
            <FontAwesomeIcon
              icon={faRobot}
              fixedWidth
              className="me-2 text-muted"
            />
            System User
            {isSystem && (
              <FontAwesomeIcon icon={faCheck} className="ms-2 text-primary" />
            )}
          </Dropdown.Item>
          <Dropdown.Item onClick={onCompareUser}>
            <FontAwesomeIcon
              icon={faBalanceScale}
              fixedWidth
              className="me-2 text-muted"
            />
            Compare User
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Header>
            <FontAwesomeIcon
              icon={faDownload}
              fixedWidth
              className="me-2 text-muted"
            />
            Export
          </Dropdown.Header>
          {EXPORT_OPTIONS.map((option) => (
            <Dropdown.Item key={option.key} onClick={() => handleSelect(option)}>
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="lead fw-normal">
            <FontAwesomeIcon icon={faDownload} className="text-muted mx-2" />
            Export
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <FieldLabel>Export Name</FieldLabel>
            <Form.Control
              type="text"
              value={exportParams?.name || ''}
              autoFocus
              onChange={(e) =>
                setExportParams({ ...exportParams, name: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className="bg-white text-muted"
            onClick={() => setShow(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={() => handleSubmit()}>
            Export
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}

ActionsDropdown.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    external_id: PropTypes.string
  }).isRequired,
  isSystem: PropTypes.bool,
  onToggleSystemUser: PropTypes.func.isRequired,
  onAddPermission: PropTypes.func.isRequired,
  onCompareUser: PropTypes.func.isRequired
}

export default ActionsDropdown
